import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import Loader from "../../components/common/Loader";
import Footer from "../../components/common/Footer";
import Menu from "../../components/common/Menu";
import Navbar from "../../components/common/Navbar";
import StartButton from "../../components/home/StartButton";
import { useCart } from "../../contexts/CartContext";
import { tempeFabricationClientWrapper } from "../../services/tempeFabricationClientWrapper";
import PaymentForm from "../../components/checkout/PaymentForm";
import { usePageLoader } from "../../hooks/usePageLoader";
import { useCheckoutSession } from "../../hooks/useCheckoutSession";
import { useInvalidateProduct } from "../../services/tempeFabricationCachedClient";
import ShippingOptionsSelector from "../../components/checkout/ShippingOptionsSelector";
import type { Address } from "../../services/tempeFabricationClientWrapper";
import type { ShippingOption, ShippingOptionType } from "@tempefab-model/tempe-fabrication-client";

// Contiguous US states (48 states + DC, excluding Alaska and Hawaii)
const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

// Load Stripe from environment variable
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
if (!stripePublishableKey) {
  console.error("Missing REACT_APP_STRIPE_PUBLISHABLE_KEY environment variable");
}
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

// Address interface
interface AddressForm {
  streetAddress: string;
  apartmentUnit?: string;
  city: string;
  state: string;
  zipCode: string;
}

// Shipping information form interface
interface ShippingFormData {
  customerName: string;
  email: string;
  phone: string;
  billingAddress: AddressForm;
  shippingAddress: AddressForm;
  sameAsBilling: boolean;
}

// Valid state codes for validation
const VALID_STATE_CODES = US_STATES.map(s => s.code);

// Validation schema
const addressSchema = yup.object().shape({
  streetAddress: yup.string().required("Street address is required"),
  apartmentUnit: yup.string(),
  city: yup.string().required("City is required"),
  state: yup.string()
    .required("State is required")
    .oneOf(VALID_STATE_CODES, "Please select a valid state"),
  zipCode: yup.string().required("ZIP code is required").matches(/^\d{5}(-\d{4})?$/, "Invalid ZIP code"),
});

const shippingFormSchema = yup.object().shape({
  customerName: yup.string().required("Name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  phone: yup.string()
    .required("Phone is required")
    .matches(
      /^\+?1?[-.\s]?[(]?\d{3}[)]?[-.\s]?\d{3}[-.\s]?\d{4}$/,
      "Invalid phone number format"
    ),
  billingAddress: addressSchema,
  shippingAddress: addressSchema,
  sameAsBilling: yup.boolean().required().default(true),
});

const Checkout: React.FC = () => {
  usePageLoader(); // Hide loader on mount for client-side navigation

  const navigate = useNavigate();
  const { items, getTotalPrice, clearCart, getCartHash } = useCart();
  const { getClientId, setTTL, isExpired, clearSession, initializeCheckout, setPaymentIntentId: storePaymentIntentId } = useCheckoutSession();
  const { invalidateProduct } = useInvalidateProduct();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [clientSecret, setClientSecret] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [orderInfo, setOrderInfo] = useState<{ shortOrderId: string; email: string } | null>(null);
  const [selectedShippingOption, setSelectedShippingOption] = useState<ShippingOptionType | null>(null);
  const [shippingOptionError, setShippingOptionError] = useState<string>("");
  const [addressSuggestions, setAddressSuggestions] = useState<Address[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState<number | null>(null);
  const [priceBreakdown, setPriceBreakdown] = useState<{
    productSubtotal: number;
    shippingCharge: number;
    estimatedTax: number;
    additionalFee?: number;
    discount?: number;
    total: number;
  } | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [shippingFormData, setShippingFormData] = useState<ShippingFormData | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ShippingFormData>({
    resolver: yupResolver(shippingFormSchema),
    defaultValues: {
      sameAsBilling: true,
      customerName: "",
      email: "",
      phone: "",
      billingAddress: {
        streetAddress: "",
        apartmentUnit: "",
        city: "",
        state: "",
        zipCode: "",
      },
      shippingAddress: {
        streetAddress: "",
        apartmentUnit: "",
        city: "",
        state: "",
        zipCode: "",
      },
    },
    mode: "onSubmit",
  });

  const sameAsBilling = watch("sameAsBilling");
  const billingAddress = watch("billingAddress");

  // Aggregate shipping options from cart items (already have shipping options from when they were added)
  const availableShippingOptions = useMemo(() => {
    if (items.length === 0) return [];

    // Collect all shipping options from cart items
    const optionsMap = new Map<ShippingOptionType, ShippingOption>();

    items.forEach(item => {
      if (item.shippingOptions) {
        item.shippingOptions.forEach((option: ShippingOption) => {
          if (option.type) {
            // Use the most expensive cost for each shipping type (to cover all products)
            const existing = optionsMap.get(option.type);
            if (!existing || (option.cost && existing.cost && option.cost > existing.cost)) {
              optionsMap.set(option.type, option);
            }
          }
        });
      }
    });

    // Convert map to array and sort by cost
    return Array.from(optionsMap.values()).sort((a, b) => (a.cost || 0) - (b.cost || 0));
  }, [items]);

  // Auto-select the first (cheapest) shipping option when available
  useEffect(() => {
    if (availableShippingOptions.length > 0 && !selectedShippingOption) {
      const firstOption = availableShippingOptions[0];
      if (firstOption.type) {
        setSelectedShippingOption(firstOption.type);
      }
    }
  }, [availableShippingOptions, selectedShippingOption]);

  // Calculate shipping cost based on selected option
  const shippingCost = useMemo(() => {
    if (!selectedShippingOption) return 0;
    const option = availableShippingOptions.find(opt => opt.type === selectedShippingOption);
    return option?.cost || 0;
  }, [selectedShippingOption, availableShippingOptions]);

  // Initialize checkout session if not already initialized
  useEffect(() => {
    if (items.length > 0 && !getClientId()) {
      console.log('[Checkout] No clientId found on mount, initializing checkout session');
      // Initialize session with current cart hash
      const cartHash = getCartHash();
      const clientId = initializeCheckout(cartHash);
      console.log('[Checkout] Session initialized on mount with clientId:', clientId);
    }
  }, [items, getClientId, getCartHash, initializeCheckout]);

  // Pre-fill form with existing shipping data when returning from step 2
  useEffect(() => {
    if (shippingFormData && step === 1) {
      console.log("[Checkout] Pre-filling form with existing shipping data");
      setValue("customerName", shippingFormData.customerName);
      setValue("email", shippingFormData.email);
      setValue("phone", shippingFormData.phone);
      setValue("billingAddress.streetAddress", shippingFormData.billingAddress.streetAddress);
      setValue("billingAddress.apartmentUnit", shippingFormData.billingAddress.apartmentUnit || "");
      setValue("billingAddress.city", shippingFormData.billingAddress.city);
      setValue("billingAddress.state", shippingFormData.billingAddress.state);
      setValue("billingAddress.zipCode", shippingFormData.billingAddress.zipCode);
      setValue("shippingAddress.streetAddress", shippingFormData.shippingAddress.streetAddress);
      setValue("shippingAddress.apartmentUnit", shippingFormData.shippingAddress.apartmentUnit || "");
      setValue("shippingAddress.city", shippingFormData.shippingAddress.city);
      setValue("shippingAddress.state", shippingFormData.shippingAddress.state);
      setValue("shippingAddress.zipCode", shippingFormData.shippingAddress.zipCode);
      setValue("sameAsBilling", shippingFormData.sameAsBilling);
    }
  }, [shippingFormData, step, setValue]);

  // Auto-sync shipping address with billing address when "Same as billing" is checked
  useEffect(() => {
    if (sameAsBilling && billingAddress) {
      setValue("shippingAddress.streetAddress", billingAddress.streetAddress || "");
      setValue("shippingAddress.apartmentUnit", billingAddress.apartmentUnit || "");
      setValue("shippingAddress.city", billingAddress.city || "");
      setValue("shippingAddress.state", billingAddress.state || "");
      setValue("shippingAddress.zipCode", billingAddress.zipCode || "");
    }
  }, [sameAsBilling, billingAddress, setValue]);

  // Redirect to checkout-expired if session expires and clear payment intent state
  useEffect(() => {
    if (isExpired && step !== 3) {
      console.warn('[Checkout] Session expired, clearing payment intent state and redirecting');
      // Pass payment intent ID via navigation state for cancellation
      const intentId = paymentIntentId;
      // Clear payment intent related state
      setPaymentIntentId(null);
      setShippingFormData(null);
      setPriceBreakdown(null);
      setClientSecret("");
      navigate("/checkout-expired", { state: { paymentIntentId: intentId } });
    }
  }, [isExpired, step, navigate, paymentIntentId]);

  // Detect when session is cleared due to cart changes and return to Step 1
  useEffect(() => {
    // Only check if we're on Step 2 (payment) and have a payment intent
    if (step === 2 && paymentIntentId) {
      const clientId = getClientId();

      // If clientId is null, session was cleared (likely due to cart change)
      if (!clientId) {
        console.warn('[Checkout] Session cleared while on Step 2, returning to Step 1');

        // Clear payment intent related state
        setPaymentIntentId(null);
        setShippingFormData(null);
        setPriceBreakdown(null);
        setClientSecret("");

        // Return to Step 1
        setStep(1);
      }
    }
  }, [step, paymentIntentId, getClientId]);

  // Redirect if cart is empty (but not if we're on step 3 - confirmation)
  if (items.length === 0 && step !== 3) {
    return (
      <>
        <Helmet>
          <title>Checkout | Tempe Fabrication</title>
          <link rel="stylesheet" type="text/css" href="/home/assets/css/home_1_style.css" />
          <link rel="stylesheet" type="text/css" href="/innerpages/assets/css/innerpages.css" />
        </Helmet>
        <div className="inner-pages-style1">
          <Loader />
          <Menu />
          <div className="smooth-scroll-content" id="scrollsmoother-container">
            <Navbar currentPage="products" />
            <section className="tc-product-detail-section" style={{ padding: "100px 0" }}>
              <div className="container">
                <div className="text-center">
                  <h2>Your cart is empty</h2>
                  <p className="mt-3">Add some products to your cart before checking out.</p>
                  <a
                    href="/products"
                    className="butn border rounded-pill hover-bg-black bg-orange1 text-white mt-4"
                  >
                    <span>Continue Shopping</span>
                  </a>
                </div>
              </div>
            </section>
            <Footer />
          </div>
          <StartButton />
        </div>
      </>
    );
  }

  // Copy billing address to shipping address when checkbox changes
  const handleSameAsBillingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setValue("sameAsBilling", checked);

    if (checked) {
      const billingAddress = watch("billingAddress");
      setValue("shippingAddress.streetAddress", billingAddress.streetAddress || "");
      setValue("shippingAddress.apartmentUnit", billingAddress.apartmentUnit || "");
      setValue("shippingAddress.city", billingAddress.city || "");
      setValue("shippingAddress.state", billingAddress.state || "");
      setValue("shippingAddress.zipCode", billingAddress.zipCode || "");
    }
  };

  // Apply a suggested address from the backend
  const applySuggestedAddress = (address: Address, targetType: "billing" | "shipping" | "both") => {
    if (targetType === "billing" || targetType === "both") {
      setValue("billingAddress.streetAddress", address.streetAddress);
      setValue("billingAddress.apartmentUnit", address.apartmentUnit || "");
      setValue("billingAddress.city", address.city);
      setValue("billingAddress.state", address.state);
      setValue("billingAddress.zipCode", address.zipCode);
    }

    if (targetType === "shipping" || targetType === "both") {
      setValue("shippingAddress.streetAddress", address.streetAddress);
      setValue("shippingAddress.apartmentUnit", address.apartmentUnit || "");
      setValue("shippingAddress.city", address.city);
      setValue("shippingAddress.state", address.state);
      setValue("shippingAddress.zipCode", address.zipCode);
    }

    // Clear the suggestions and error after applying
    setAddressSuggestions([]);
    setSelectedSuggestionIndex(null);
    setError("");
  };

  // Process payment intent (address validation happens on backend)
  const processPaymentIntent = async (data: ShippingFormData) => {
    try {
      setLoading(true);
      setError("");

      // Get clientId from checkout session
      const clientId = getClientId();
      console.log("[Checkout] Retrieved clientId for payment intent:", clientId);
      if (!clientId) {
        console.error("[Checkout] ERROR: No clientId found!");
        setError("Checkout session not initialized. Please try again from your cart.");
        setLoading(false);
        return;
      }

      // If "Same as billing" is checked, use billing address for shipping
      const shippingAddress = data.sameAsBilling ? data.billingAddress : data.shippingAddress;

      // Convert cart items to InvoicedProduct format
      const productList = items.map(item => ({
        productId: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        sku: item.sku,
        total: (item.discount ? item.price - (item.price * item.discount / 100) : item.price) * item.quantity,
        customization: item.customization, // Include customization if selected
      }));

      // Prepare payment intent data
      const paymentIntentData = {
        email: data.email,
        customerName: data.customerName,
        phone: data.phone,
        billingAddress: {
          streetAddress: data.billingAddress.streetAddress,
          apartmentUnit: data.billingAddress.apartmentUnit,
          city: data.billingAddress.city,
          state: data.billingAddress.state,
          zipCode: data.billingAddress.zipCode,
        },
        shippingAddress: {
          streetAddress: shippingAddress.streetAddress,
          apartmentUnit: shippingAddress.apartmentUnit,
          city: shippingAddress.city,
          state: shippingAddress.state,
          zipCode: shippingAddress.zipCode,
        },
        productList,
        clientId, // Include clientId for idempotency
        shippingOption: selectedShippingOption || undefined, // Include selected shipping option
      };

      let response;

      // Check if we're updating an existing payment intent or creating a new one
      if (paymentIntentId) {
        console.log("[Checkout] Updating existing payment intent:", paymentIntentId);
        response = await tempeFabricationClientWrapper.updatePaymentIntent({
          id: paymentIntentId,
          update: paymentIntentData,
        });
      } else {
        console.log("[Checkout] Creating new payment intent with clientId:", clientId);
        response = await tempeFabricationClientWrapper.createPaymentIntent(paymentIntentData);
      }

      // Payment intent created successfully (details not logged for security)

      if (response.clientSecretId && response.ttl && response.shortOrderId && response.id) {
        // Store TTL from response
        setTTL(response.ttl);

        // Store payment intent ID for potential updates
        setPaymentIntentId(response.id);
        // Store payment intent ID in session for cancellation if checkout expires
        storePaymentIntentId(response.id);

        // Store shipping form data for display in order summary
        setShippingFormData(data);

        // Store order info for confirmation page
        setOrderInfo({
          shortOrderId: response.shortOrderId,
          email: data.email,
        });

        // Store price breakdown from API response
        setPriceBreakdown({
          productSubtotal: response.productSubtotal || 0,
          shippingCharge: response.shippingCharge || 0,
          estimatedTax: response.estimatedTax || 0,
          additionalFee: response.additionalFee,
          discount: response.discount,
          total: response.total || 0,
        });

        setClientSecret(response.clientSecretId);
        console.log("[Checkout] Moving to step 2 (payment)");
        setStep(2);
      } else {
        console.error("[Checkout] Payment intent response missing required fields");
        setError("Failed to initialize payment. Please try again.");
      }
    } catch (err: any) {
      console.error("[Checkout] Error creating payment intent:", err);

      // Handle errors parsed by the service wrapper
      if (err.type === 'ProductUnavailable' && err.productId) {
        console.error("[Checkout] Product unavailable:", { productId: err.productId });

        // Invalidate the product cache so next time user views it, they get fresh data
        invalidateProduct(err.productId);

        // Try to map productId to product name from cart items
        const unavailableProduct = items.find(item => item.id === err.productId);
        const productName = unavailableProduct ? unavailableProduct.name : `Product ID: ${err.productId}`;

        setError(
          `Sorry, "${productName}" is no longer available. Please remove it from your cart and try again.`
        );
      }
      else if (err.type === 'InvalidAddressException') {
        console.error("[Checkout] Invalid address:", err.message);
        setError(err.message || 'The address could not be verified.');

        // If there are suggested addresses, store them for selection
        if (err.possibleAddresses && err.possibleAddresses.length > 0) {
          setAddressSuggestions(err.possibleAddresses);
          setSelectedSuggestionIndex(null);
        }
      }
      else if (err.type === 'ValidationException') {
        console.error("[Checkout] Validation error:", err.message);
        setError(err.message || 'Validation failed. Please check your information and try again.');
      }
      else if (err.type === 'InvalidInput') {
        console.error("[Checkout] Invalid input error:", err.message);
        setError(err.message || 'Invalid input. Please check your information and try again.');
      }
      else {
        // Generic error fallback
        const errorMessage = err.message || "An error occurred while processing your request. Please try again.";
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle shipping form submission
  const onSubmitShipping = async (data: ShippingFormData) => {
    // Form submission started (data not logged for privacy)
    try {
      setError("");
      setShippingOptionError("");
      // Clear any previous address suggestions
      setAddressSuggestions([]);
      setSelectedSuggestionIndex(null);

      // Validate shipping option is selected
      if (!selectedShippingOption) {
        setShippingOptionError("Please select a shipping method");
        return;
      }

      // Address validation happens on the backend during payment intent creation
      await processPaymentIntent(data);
    } catch (err: any) {
      console.error("[Checkout] Error in form submission:", err);
      setError(err.message || "An error occurred. Please try again.");
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    console.log("[Checkout] Payment successful! Clearing cart, session, and payment intent state");
    clearCart();
    clearSession(); // Clear checkout session on successful payment
    // Clear payment intent state
    setPaymentIntentId(null);
    setShippingFormData(null);
    setStep(3);
  };

  return (
    <>
      <Helmet>
        <title>Checkout | Tempe Fabrication</title>
        <link rel="stylesheet" type="text/css" href="/home/assets/css/home_1_style.css" />
        <link rel="stylesheet" type="text/css" href="/innerpages/assets/css/innerpages.css" />
        <script src="/common/assets/js/common_js.js"></script>
        <script src="/innerpages/assets/js/innerpages.js"></script>
      </Helmet>
      <div className="inner-pages-style1">
        <Loader />
        <Menu />
        <div className="smooth-scroll-content" id="scrollsmoother-container">
          <Navbar currentPage="products" />

          <header className="tc-inner-header-style1">
            <div className="container">
              <div className="info col-lg-8">
                <h2 className="js-title">Checkout</h2>
              </div>
            </div>
          </header>

          <section className="tc-checkout-section" style={{ padding: "80px 0" }}>
            <div className="container">
              <div className="row">
                {/* Left Column - Form */}
                <div className={step === 3 ? "col-lg-12" : "col-lg-7"}>
                  {/* Progress Steps */}
                  <div style={{ marginBottom: "40px" }}>
                    <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        opacity: step >= 1 ? 1 : 0.5,
                      }}>
                        <div style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          backgroundColor: step >= 1 ? (step === 1 ? "var(--color-orange1)" : "#28a745") : "#ddd",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "600",
                        }}>
                          {step > 1 ? "✓" : "1"}
                        </div>
                        <span style={{ fontSize: "16px", fontWeight: "500" }}>Shipping</span>
                      </div>
                      <div style={{ flex: 1, height: "2px", backgroundColor: step >= 2 ? "#28a745" : "#ddd" }} />
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        opacity: step >= 2 ? 1 : 0.5,
                      }}>
                        <div style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          backgroundColor: step >= 2 ? (step === 2 ? "var(--color-orange1)" : "#28a745") : "#ddd",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "600",
                        }}>
                          {step > 2 ? "✓" : "2"}
                        </div>
                        <span style={{ fontSize: "16px", fontWeight: "500" }}>Payment</span>
                      </div>
                      <div style={{ flex: 1, height: "2px", backgroundColor: step >= 3 ? "#28a745" : "#ddd" }} />
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        opacity: step === 3 ? 1 : 0.5,
                      }}>
                        <div style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          backgroundColor: step === 3 ? "#28a745" : "#ddd",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "600",
                        }}>
                          3
                        </div>
                        <span style={{ fontSize: "16px", fontWeight: "500" }}>Confirmed</span>
                      </div>
                    </div>
                  </div>

                  {/* Step 1: Shipping Information */}
                  {step === 1 && (
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      // If "Same as billing" is checked, copy billing address to shipping before validation
                      if (sameAsBilling) {
                        const billing = watch("billingAddress");
                        setValue("shippingAddress.streetAddress", billing.streetAddress || "");
                        setValue("shippingAddress.apartmentUnit", billing.apartmentUnit || "");
                        setValue("shippingAddress.city", billing.city || "");
                        setValue("shippingAddress.state", billing.state || "");
                        setValue("shippingAddress.zipCode", billing.zipCode || "");
                      }
                      // Now trigger the form validation and submission
                      await handleSubmit(onSubmitShipping)(e);
                    }}>
                      <div style={{
                        backgroundColor: "#f9f9f9",
                        padding: "30px",
                        borderRadius: "8px",
                        marginBottom: "20px",
                      }}>
                        <h3 style={{ marginBottom: "20px", fontSize: "20px" }}>Contact Information</h3>

                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                              Full Name *
                            </label>
                            <input
                              {...register("customerName")}
                              type="text"
                              className="form-control"
                              style={{
                                width: "100%",
                                padding: "10px 15px",
                                border: errors.customerName ? "1px solid #dc3545" : "1px solid #ddd",
                                borderRadius: "4px",
                              }}
                            />
                            {errors.customerName && (
                              <span style={{ color: "#dc3545", fontSize: "14px" }}>
                                {errors.customerName.message}
                              </span>
                            )}
                          </div>

                          <div className="col-md-6 mb-3">
                            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                              Email *
                            </label>
                            <input
                              {...register("email")}
                              type="email"
                              className="form-control"
                              style={{
                                width: "100%",
                                padding: "10px 15px",
                                border: errors.email ? "1px solid #dc3545" : "1px solid #ddd",
                                borderRadius: "4px",
                              }}
                            />
                            {errors.email && (
                              <span style={{ color: "#dc3545", fontSize: "14px" }}>
                                {errors.email.message}
                              </span>
                            )}
                          </div>

                          <div className="col-12 mb-3">
                            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                              Phone *
                            </label>
                            <input
                              {...register("phone")}
                              type="tel"
                              className="form-control"
                              placeholder="+1 (555) 123-4567"
                              style={{
                                width: "100%",
                                padding: "10px 15px",
                                border: errors.phone ? "1px solid #dc3545" : "1px solid #ddd",
                                borderRadius: "4px",
                              }}
                            />
                            {errors.phone && (
                              <span style={{ color: "#dc3545", fontSize: "14px" }}>
                                {errors.phone.message}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Billing Address */}
                      <div style={{
                        backgroundColor: "#f9f9f9",
                        padding: "30px",
                        borderRadius: "8px",
                        marginBottom: "20px",
                      }}>
                        <h3 style={{ marginBottom: "20px", fontSize: "20px" }}>Billing Address</h3>

                        <div className="row">
                          <div className="col-12 mb-3">
                            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                              Street Address *
                            </label>
                            <input
                              {...register("billingAddress.streetAddress")}
                              type="text"
                              className="form-control"
                              placeholder="Street address"
                              style={{
                                width: "100%",
                                padding: "10px 15px",
                                border: errors.billingAddress?.streetAddress ? "1px solid #dc3545" : "1px solid #ddd",
                                borderRadius: "4px",
                              }}
                            />
                            {errors.billingAddress?.streetAddress && (
                              <span style={{ color: "#dc3545", fontSize: "14px" }}>
                                {errors.billingAddress.streetAddress.message}
                              </span>
                            )}
                          </div>

                          <div className="col-12 mb-3">
                            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                              Apartment, Suite, etc. (optional)
                            </label>
                            <input
                              {...register("billingAddress.apartmentUnit")}
                              type="text"
                              className="form-control"
                              style={{
                                width: "100%",
                                padding: "10px 15px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                              }}
                            />
                          </div>

                          <div className="col-md-6 mb-3">
                            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                              City *
                            </label>
                            <input
                              {...register("billingAddress.city")}
                              type="text"
                              className="form-control"
                              style={{
                                width: "100%",
                                padding: "10px 15px",
                                border: errors.billingAddress?.city ? "1px solid #dc3545" : "1px solid #ddd",
                                borderRadius: "4px",
                              }}
                            />
                            {errors.billingAddress?.city && (
                              <span style={{ color: "#dc3545", fontSize: "14px" }}>
                                {errors.billingAddress.city.message}
                              </span>
                            )}
                          </div>

                          <div className="col-md-3 mb-3">
                            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                              State *
                            </label>
                            <select
                              {...register("billingAddress.state")}
                              className="form-control"
                              style={{
                                width: "100%",
                                padding: "10px 15px",
                                border: errors.billingAddress?.state ? "1px solid #dc3545" : "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                cursor: "pointer",
                              }}
                            >
                              <option value="">Select</option>
                              {US_STATES.map((state) => (
                                <option key={state.code} value={state.code}>
                                  {state.code}
                                </option>
                              ))}
                            </select>
                            {errors.billingAddress?.state && (
                              <span style={{ color: "#dc3545", fontSize: "14px" }}>
                                {errors.billingAddress.state.message}
                              </span>
                            )}
                          </div>

                          <div className="col-md-3 mb-3">
                            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                              ZIP Code *
                            </label>
                            <input
                              {...register("billingAddress.zipCode")}
                              type="text"
                              className="form-control"
                              placeholder="12345"
                              style={{
                                width: "100%",
                                padding: "10px 15px",
                                border: errors.billingAddress?.zipCode ? "1px solid #dc3545" : "1px solid #ddd",
                                borderRadius: "4px",
                              }}
                            />
                            {errors.billingAddress?.zipCode && (
                              <span style={{ color: "#dc3545", fontSize: "14px" }}>
                                {errors.billingAddress.zipCode.message}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Shipping Address */}
                      <div style={{
                        backgroundColor: "#f9f9f9",
                        padding: "30px",
                        borderRadius: "8px",
                        marginBottom: "20px",
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                          <h3 style={{ fontSize: "20px", margin: 0 }}>Shipping Address</h3>
                          <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={sameAsBilling}
                              onChange={handleSameAsBillingChange}
                              style={{ width: "18px", height: "18px" }}
                            />
                            <span>Same as billing</span>
                          </label>
                        </div>

                        {!sameAsBilling && (
                          <div className="row">
                            <div className="col-12 mb-3">
                              <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                                Street Address *
                              </label>
                              <input
                                {...register("shippingAddress.streetAddress")}
                                type="text"
                                className="form-control"
                                placeholder="Street address"
                                style={{
                                  width: "100%",
                                  padding: "10px 15px",
                                  border: errors.shippingAddress?.streetAddress ? "1px solid #dc3545" : "1px solid #ddd",
                                  borderRadius: "4px",
                                }}
                              />
                              {errors.shippingAddress?.streetAddress && (
                                <span style={{ color: "#dc3545", fontSize: "14px" }}>
                                  {errors.shippingAddress.streetAddress.message}
                                </span>
                              )}
                            </div>

                            <div className="col-12 mb-3">
                              <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                                Apartment, Suite, etc. (optional)
                              </label>
                              <input
                                {...register("shippingAddress.apartmentUnit")}
                                type="text"
                                className="form-control"
                                style={{
                                  width: "100%",
                                  padding: "10px 15px",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                }}
                              />
                            </div>

                            <div className="col-md-6 mb-3">
                              <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                                City *
                              </label>
                              <input
                                {...register("shippingAddress.city")}
                                type="text"
                                className="form-control"
                                style={{
                                  width: "100%",
                                  padding: "10px 15px",
                                  border: errors.shippingAddress?.city ? "1px solid #dc3545" : "1px solid #ddd",
                                  borderRadius: "4px",
                                }}
                              />
                              {errors.shippingAddress?.city && (
                                <span style={{ color: "#dc3545", fontSize: "14px" }}>
                                  {errors.shippingAddress.city.message}
                                </span>
                              )}
                            </div>

                            <div className="col-md-3 mb-3">
                              <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                                State *
                              </label>
                              <select
                                {...register("shippingAddress.state")}
                                className="form-control"
                                style={{
                                  width: "100%",
                                  padding: "10px 15px",
                                  border: errors.shippingAddress?.state ? "1px solid #dc3545" : "1px solid #ddd",
                                  borderRadius: "4px",
                                  backgroundColor: "#fff",
                                  cursor: "pointer",
                                }}
                              >
                                <option value="">Select</option>
                                {US_STATES.map((state) => (
                                  <option key={state.code} value={state.code}>
                                    {state.code}
                                  </option>
                                ))}
                              </select>
                              {errors.shippingAddress?.state && (
                                <span style={{ color: "#dc3545", fontSize: "14px" }}>
                                  {errors.shippingAddress.state.message}
                                </span>
                              )}
                            </div>

                            <div className="col-md-3 mb-3">
                              <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                                ZIP Code *
                              </label>
                              <input
                                {...register("shippingAddress.zipCode")}
                                type="text"
                                className="form-control"
                                placeholder="12345"
                                style={{
                                  width: "100%",
                                  padding: "10px 15px",
                                  border: errors.shippingAddress?.zipCode ? "1px solid #dc3545" : "1px solid #ddd",
                                  borderRadius: "4px",
                                }}
                              />
                              {errors.shippingAddress?.zipCode && (
                                <span style={{ color: "#dc3545", fontSize: "14px" }}>
                                  {errors.shippingAddress.zipCode.message}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Shipping Options */}
                      {availableShippingOptions.length > 0 && (
                        <ShippingOptionsSelector
                          shippingOptions={availableShippingOptions}
                          selectedOption={selectedShippingOption}
                          onSelect={(option) => {
                            setSelectedShippingOption(option);
                            setShippingOptionError("");
                          }}
                          error={shippingOptionError}
                        />
                      )}

                      {error && (
                        <div style={{
                          backgroundColor: addressSuggestions.length > 0 ? "#fff3cd" : (error.includes("no longer available") ? "#fff3cd" : "#f8d7da"),
                          color: addressSuggestions.length > 0 ? "#856404" : (error.includes("no longer available") ? "#856404" : "#721c24"),
                          padding: "15px",
                          borderRadius: "4px",
                          marginBottom: "20px",
                          border: addressSuggestions.length > 0 ? "1px solid #ffc107" : (error.includes("no longer available") ? "1px solid #ffc107" : "1px solid #f5c6cb"),
                        }}>
                          <strong>
                            {addressSuggestions.length > 0 ? "Address Verification Required" : (error.includes("no longer available") ? "Product Unavailable" : "Error")}
                          </strong>
                          <div style={{ marginTop: "8px" }}>
                            {error}
                          </div>
                        </div>
                      )}

                      {/* Address Suggestions Radio Buttons */}
                      {addressSuggestions.length > 0 && (
                        <div style={{
                          backgroundColor: "#f9f9f9",
                          padding: "20px",
                          borderRadius: "8px",
                          marginBottom: "20px",
                          border: "2px solid var(--color-orange1, #ff6b35)",
                        }}>
                          <h4 style={{ marginBottom: "15px", fontSize: "16px", fontWeight: "600" }}>
                            Did you mean one of these addresses?
                          </h4>
                          <p style={{ marginBottom: "15px", color: "#666", fontSize: "14px" }}>
                            Select a suggested address{!sameAsBilling && " and choose which address to update"}:
                          </p>
                          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            {addressSuggestions.map((address, index) => (
                              <label
                                key={index}
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: "12px",
                                  padding: "15px",
                                  border: selectedSuggestionIndex === index ? "2px solid var(--color-orange1, #ff6b35)" : "1px solid #ddd",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  backgroundColor: selectedSuggestionIndex === index ? "#fff5eb" : "#fff",
                                  transition: "all 0.2s ease",
                                }}
                              >
                                <input
                                  type="radio"
                                  name="addressSuggestion"
                                  checked={selectedSuggestionIndex === index}
                                  onChange={() => setSelectedSuggestionIndex(index)}
                                  style={{
                                    width: "20px",
                                    height: "20px",
                                    marginTop: "2px",
                                    accentColor: "var(--color-orange1, #ff6b35)",
                                  }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: "500" }}>{address.streetAddress}</div>
                                  {address.apartmentUnit && (
                                    <div style={{ color: "#666" }}>{address.apartmentUnit}</div>
                                  )}
                                  <div style={{ color: "#666" }}>
                                    {address.city}, {address.state} {address.zipCode}
                                  </div>
                                </div>
                              </label>
                            ))}
                          </div>
                          <div style={{ marginTop: "15px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            {sameAsBilling ? (
                              /* Single button when same as billing - applies to both addresses */
                              <button
                                type="button"
                                onClick={() => {
                                  if (selectedSuggestionIndex !== null) {
                                    applySuggestedAddress(addressSuggestions[selectedSuggestionIndex], "both");
                                  }
                                }}
                                disabled={selectedSuggestionIndex === null}
                                className="butn border rounded-pill hover-bg-black bg-orange1 text-white"
                                style={{
                                  padding: "10px 20px",
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  opacity: selectedSuggestionIndex === null ? 0.5 : 1,
                                  cursor: selectedSuggestionIndex === null ? "not-allowed" : "pointer",
                                }}
                              >
                                <span>Use Selected Address</span>
                              </button>
                            ) : (
                              /* Two buttons when billing and shipping are different */
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (selectedSuggestionIndex !== null) {
                                      applySuggestedAddress(addressSuggestions[selectedSuggestionIndex], "billing");
                                    }
                                  }}
                                  disabled={selectedSuggestionIndex === null}
                                  className="butn border rounded-pill hover-bg-black bg-orange1 text-white"
                                  style={{
                                    padding: "10px 20px",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    opacity: selectedSuggestionIndex === null ? 0.5 : 1,
                                    cursor: selectedSuggestionIndex === null ? "not-allowed" : "pointer",
                                  }}
                                >
                                  <span>Set Billing Address</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (selectedSuggestionIndex !== null) {
                                      applySuggestedAddress(addressSuggestions[selectedSuggestionIndex], "shipping");
                                    }
                                  }}
                                  disabled={selectedSuggestionIndex === null}
                                  className="butn border rounded-pill hover-bg-black bg-orange1 text-white"
                                  style={{
                                    padding: "10px 20px",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    opacity: selectedSuggestionIndex === null ? 0.5 : 1,
                                    cursor: selectedSuggestionIndex === null ? "not-allowed" : "pointer",
                                  }}
                                >
                                  <span>Set Shipping Address</span>
                                </button>
                              </>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                setAddressSuggestions([]);
                                setSelectedSuggestionIndex(null);
                                setError("");
                              }}
                              className="butn border rounded-pill"
                              style={{
                                padding: "10px 20px",
                                fontSize: "14px",
                                color: "#333",
                              }}
                            >
                              <span>Keep My Address</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Display form validation errors */}
                      {Object.keys(errors).length > 0 && (
                        <>
                          <div style={{
                            backgroundColor: "#fff3cd",
                            color: "#856404",
                            padding: "15px",
                            borderRadius: "4px",
                            marginBottom: "20px",
                          }}>
                            <strong>Please fix the following errors:</strong>
                            <ul style={{ marginTop: "10px", marginBottom: "0", paddingLeft: "20px" }}>
                              {errors.customerName && <li>Name: {errors.customerName.message}</li>}
                              {errors.email && <li>Email: {errors.email.message}</li>}
                              {errors.phone && <li>Phone: {errors.phone.message}</li>}
                              {errors.billingAddress?.streetAddress && <li>Billing street: {errors.billingAddress.streetAddress.message}</li>}
                              {errors.billingAddress?.city && <li>Billing city: {errors.billingAddress.city.message}</li>}
                              {errors.billingAddress?.state && <li>Billing state: {errors.billingAddress.state.message}</li>}
                              {errors.billingAddress?.zipCode && <li>Billing ZIP: {errors.billingAddress.zipCode.message}</li>}
                              {errors.shippingAddress?.streetAddress && <li>Shipping street: {errors.shippingAddress.streetAddress.message}</li>}
                              {errors.shippingAddress?.city && <li>Shipping city: {errors.shippingAddress.city.message}</li>}
                              {errors.shippingAddress?.state && <li>Shipping state: {errors.shippingAddress.state.message}</li>}
                              {errors.shippingAddress?.zipCode && <li>Shipping ZIP: {errors.shippingAddress.zipCode.message}</li>}
                              {errors.sameAsBilling && <li>Checkbox error: {errors.sameAsBilling.message}</li>}
                              {/* Catch-all for any other errors */}
                              {Object.keys(errors).length > 0 &&
                               !errors.customerName && !errors.email && !errors.phone &&
                               !errors.billingAddress && !errors.shippingAddress && !errors.sameAsBilling && (
                                <li>Unknown validation error - check console for details</li>
                              )}
                            </ul>
                          </div>
                        </>
                      )}

                      <button
                        type="submit"
                        disabled={loading}
                        className="butn border rounded-pill hover-bg-black bg-orange1 text-white"
                        style={{
                          width: "100%",
                          padding: "15px",
                          fontSize: "16px",
                          fontWeight: "600",
                          opacity: loading ? 0.6 : 1,
                          cursor: loading ? "not-allowed" : "pointer",
                        }}
                      >
                        <span>{loading ? "Processing..." : "Continue to Payment"}</span>
                      </button>
                    </form>
                  )}

                  {/* Step 2: Payment */}
                  {step === 2 && clientSecret && (
                    <>
                      {/* Order Summary Card - Review before payment */}
                      {shippingFormData && priceBreakdown && (
                        <div style={{
                          backgroundColor: "#f9f9f9",
                          padding: "30px",
                          borderRadius: "8px",
                          marginBottom: "30px",
                        }}>
                          <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "20px"
                          }}>
                            <h3 style={{ fontSize: "20px", margin: 0 }}>Review Your Order</h3>
                            <button
                              onClick={() => {
                                console.log("[Checkout] Make Changes clicked, returning to step 1");
                                setStep(1);
                              }}
                              className="butn border rounded-pill"
                              style={{
                                padding: "8px 20px",
                                fontSize: "14px",
                                color: "#333",
                              }}
                            >
                              <span>Make Changes</span>
                            </button>
                          </div>

                          {/* Contact Information */}
                          <div style={{ marginBottom: "20px" }}>
                            <h4 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "10px", color: "#666" }}>
                              Contact Information
                            </h4>
                            <div style={{ paddingLeft: "15px" }}>
                              <p style={{ margin: "5px 0" }}><strong>Name:</strong> {shippingFormData.customerName}</p>
                              <p style={{ margin: "5px 0" }}><strong>Email:</strong> {shippingFormData.email}</p>
                              <p style={{ margin: "5px 0" }}><strong>Phone:</strong> {shippingFormData.phone}</p>
                            </div>
                          </div>

                          {/* Billing Address */}
                          <div style={{ marginBottom: "20px" }}>
                            <h4 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "10px", color: "#666" }}>
                              Billing Address
                            </h4>
                            <div style={{ paddingLeft: "15px" }}>
                              <p style={{ margin: "5px 0" }}>{shippingFormData.billingAddress.streetAddress}</p>
                              {shippingFormData.billingAddress.apartmentUnit && (
                                <p style={{ margin: "5px 0" }}>{shippingFormData.billingAddress.apartmentUnit}</p>
                              )}
                              <p style={{ margin: "5px 0" }}>
                                {shippingFormData.billingAddress.city}, {shippingFormData.billingAddress.state} {shippingFormData.billingAddress.zipCode}
                              </p>
                            </div>
                          </div>

                          {/* Shipping Address */}
                          <div style={{ marginBottom: "20px" }}>
                            <h4 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "10px", color: "#666" }}>
                              Shipping Address
                            </h4>
                            <div style={{ paddingLeft: "15px" }}>
                              <p style={{ margin: "5px 0" }}>{shippingFormData.shippingAddress.streetAddress}</p>
                              {shippingFormData.shippingAddress.apartmentUnit && (
                                <p style={{ margin: "5px 0" }}>{shippingFormData.shippingAddress.apartmentUnit}</p>
                              )}
                              <p style={{ margin: "5px 0" }}>
                                {shippingFormData.shippingAddress.city}, {shippingFormData.shippingAddress.state} {shippingFormData.shippingAddress.zipCode}
                              </p>
                            </div>
                          </div>

                          {/* Shipping Method */}
                          {selectedShippingOption && (
                            <div style={{ marginBottom: "20px" }}>
                              <h4 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "10px", color: "#666" }}>
                                Shipping Method
                              </h4>
                              <div style={{ paddingLeft: "15px" }}>
                                <p style={{ margin: "5px 0" }}>
                                  <strong>{selectedShippingOption.replace(/_/g, " ")}</strong>
                                  {" - $"}{priceBreakdown.shippingCharge.toFixed(2)}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Order Items */}
                          <div style={{ marginBottom: "20px" }}>
                            <h4 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "10px", color: "#666" }}>
                              Order Items
                            </h4>
                            <div style={{ paddingLeft: "15px" }}>
                              {items.map((item) => {
                                const itemPrice = item.discount
                                  ? item.price - (item.price * item.discount / 100)
                                  : item.price;
                                const customizationCost = item.customization?.additionalCost || 0;
                                const priceWithCustomization = itemPrice + customizationCost;
                                return (
                                  <div key={item.cartItemId} style={{
                                    marginBottom: "10px",
                                    paddingBottom: "10px",
                                    borderBottom: "1px solid #ddd"
                                  }}>
                                    <p style={{ margin: "5px 0", fontWeight: "500" }}>
                                      {item.name} x {item.quantity}
                                    </p>
                                    {item.customization && (
                                      <p style={{ margin: "3px 0", fontSize: "13px", color: "#666", fontStyle: "italic" }}>
                                        {item.customization.name}
                                        {customizationCost > 0 && ` (+$${customizationCost.toFixed(2)})`}
                                      </p>
                                    )}
                                    <p style={{ margin: "5px 0", color: "#666" }}>
                                      ${(priceWithCustomization * item.quantity).toFixed(2)}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Price Breakdown */}
                          <div style={{
                            borderTop: "2px solid #333",
                            paddingTop: "15px",
                            marginTop: "15px"
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                              <span>Subtotal:</span>
                              <span>${priceBreakdown.productSubtotal.toFixed(2)}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                              <span>Shipping:</span>
                              <span>${priceBreakdown.shippingCharge.toFixed(2)}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                              <span>Tax:</span>
                              <span>${priceBreakdown.estimatedTax.toFixed(2)}</span>
                            </div>
                            {(priceBreakdown.additionalFee != null && priceBreakdown.additionalFee > 0) && (
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                                <span>Additional Fee:</span>
                                <span>${priceBreakdown.additionalFee.toFixed(2)}</span>
                              </div>
                            )}
                            {(priceBreakdown.discount != null && priceBreakdown.discount > 0) && (
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", color: "#28a745" }}>
                                <span>Discount:</span>
                                <span>-${priceBreakdown.discount.toFixed(2)}</span>
                              </div>
                            )}
                            <div style={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontSize: "18px",
                              fontWeight: "700",
                              marginTop: "12px",
                              paddingTop: "12px",
                              borderTop: "1px solid #ddd"
                            }}>
                              <span>Total:</span>
                              <span>${priceBreakdown.total.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Stripe Payment Form */}
                      {!stripePromise ? (
                        <div style={{ padding: "20px", backgroundColor: "#fee", border: "1px solid #c00", borderRadius: "4px" }}>
                          <strong>Payment system unavailable.</strong> Please contact support.
                        </div>
                      ) : (
                        <Elements stripe={stripePromise} options={{ clientSecret }}>
                          <PaymentForm
                            clientSecret={clientSecret}
                            onSuccess={handlePaymentSuccess}
                            onBack={() => {
                              console.log("[Checkout] Going back to step 1 from payment");
                              // Keep payment intent state so user can update it
                              setStep(1);
                            }}
                          />
                        </Elements>
                      )}
                    </>
                  )}

                  {/* Step 3: Confirmation */}
                  {step === 3 && (
                    <div style={{
                      backgroundColor: "#f9f9f9",
                      padding: "60px 40px",
                      borderRadius: "8px",
                      textAlign: "center",
                      maxWidth: "700px",
                      margin: "0 auto",
                    }}>
                      {/* Success Icon */}
                      <div style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        backgroundColor: "#28a745",
                        margin: "0 auto 30px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}>
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="white"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>

                      <h1 style={{
                        fontSize: "32px",
                        fontWeight: "600",
                        marginBottom: "15px",
                      }}>
                        Order Confirmed!
                      </h1>

                      <p style={{
                        fontSize: "16px",
                        color: "#666",
                        marginBottom: "30px",
                        lineHeight: "1.6",
                      }}>
                        {orderInfo ? (
                          <>
                            Thank you for your order. Your order ID is <strong>{orderInfo.shortOrderId}</strong>.
                            We've received your payment and will begin processing your order shortly.
                            You'll receive an email at <strong>{orderInfo.email}</strong> with your order details
                            and tracking information once your order ships.
                          </>
                        ) : (
                          <>
                            Thank you for your order. We've received your payment and will begin processing your order shortly.
                            You'll receive an email confirmation with your order details and tracking information once your order ships.
                          </>
                        )}
                      </p>

                      <div style={{
                        display: "flex",
                        gap: "15px",
                        justifyContent: "center",
                        flexWrap: "wrap",
                      }}>
                        <button
                          onClick={() => navigate("/products")}
                          className="butn border rounded-pill hover-bg-black"
                          style={{
                            padding: "12px 30px",
                            color: "#333",
                          }}
                        >
                          <span>Continue Shopping</span>
                        </button>

                        <button
                          onClick={() => navigate("/")}
                          className="butn border rounded-pill hover-bg-black bg-orange1 text-white"
                          style={{
                            padding: "12px 30px",
                          }}
                        >
                          <span>Back to Home</span>
                        </button>
                      </div>

                      <div style={{
                        marginTop: "40px",
                        paddingTop: "30px",
                        borderTop: "1px solid #ddd",
                      }}>
                        <p style={{
                          fontSize: "14px",
                          color: "#999",
                          marginBottom: "10px",
                        }}>
                          Questions about your order?
                        </p>
                        <a
                          href="/contact"
                          style={{
                            color: "var(--color-orange1)",
                            textDecoration: "none",
                            fontWeight: "500",
                          }}
                        >
                          Contact Us
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column - Order Summary */}
                {step < 3 && (
                  <div className="col-lg-5">
                    <div style={{
                      backgroundColor: "#f9f9f9",
                      padding: "30px",
                      borderRadius: "8px",
                      position: "sticky",
                      top: "20px",
                    }}>
                      <h3 style={{ marginBottom: "20px", fontSize: "20px" }}>Order Summary</h3>

                    <div style={{ marginBottom: "20px" }}>
                      {items.map((item) => {
                        const itemPrice = item.discount
                          ? item.price - (item.price * item.discount / 100)
                          : item.price;
                        const customizationCost = item.customization?.additionalCost || 0;
                        const priceWithCustomization = itemPrice + customizationCost;
                        return (
                          <div
                            key={item.cartItemId}
                            style={{
                              display: "flex",
                              gap: "15px",
                              marginBottom: "15px",
                              paddingBottom: "15px",
                              borderBottom: "1px solid #ddd",
                            }}
                          >
                            <img
                              src={item.image}
                              alt={item.name}
                              style={{
                                width: "60px",
                                height: "60px",
                                objectFit: "contain",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: "500", marginBottom: "5px" }}>{item.name}</div>
                              {item.customization && (
                                <div style={{ color: "#666", fontSize: "13px", fontStyle: "italic", marginBottom: "3px" }}>
                                  {item.customization.name}
                                  {customizationCost > 0 && ` (+$${customizationCost.toFixed(2)})`}
                                </div>
                              )}
                              <div style={{ color: "#666", fontSize: "14px" }}>Qty: {item.quantity}</div>
                            </div>
                            <div style={{ fontWeight: "600" }}>
                              ${(priceWithCustomization * item.quantity).toFixed(2)}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div style={{
                      borderTop: "2px solid #ddd",
                      paddingTop: "20px",
                      marginTop: "20px",
                    }}>
                      {priceBreakdown ? (
                        // Show final pricing from API response
                        <>
                          <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "10px",
                            fontSize: "16px",
                          }}>
                            <span>Subtotal</span>
                            <span>${priceBreakdown.productSubtotal.toFixed(2)}</span>
                          </div>
                          <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "10px",
                            fontSize: "16px",
                            color: "#666",
                          }}>
                            <span>
                              Shipping
                              {selectedShippingOption && (
                                <span style={{ fontSize: "14px", marginLeft: "5px" }}>
                                  ({selectedShippingOption.replace(/_/g, " ")})
                                </span>
                              )}
                            </span>
                            <span>${priceBreakdown.shippingCharge.toFixed(2)}</span>
                          </div>
                          <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "10px",
                            fontSize: "16px",
                            color: "#666",
                          }}>
                            <span>Tax</span>
                            <span>${priceBreakdown.estimatedTax.toFixed(2)}</span>
                          </div>
                          {(priceBreakdown.additionalFee != null && priceBreakdown.additionalFee > 0) && (
                            <div style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: "10px",
                              fontSize: "16px",
                              color: "#666",
                            }}>
                              <span>Additional Fee</span>
                              <span>${priceBreakdown.additionalFee.toFixed(2)}</span>
                            </div>
                          )}
                          {(priceBreakdown.discount != null && priceBreakdown.discount > 0) && (
                            <div style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: "10px",
                              fontSize: "16px",
                              color: "#28a745",
                            }}>
                              <span>Discount</span>
                              <span>-${priceBreakdown.discount.toFixed(2)}</span>
                            </div>
                          )}
                          <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "18px",
                            fontWeight: "600",
                            paddingTop: "15px",
                            borderTop: "1px solid #ddd",
                          }}>
                            <span>Total</span>
                            <span>${priceBreakdown.total.toFixed(2)}</span>
                          </div>
                        </>
                      ) : (
                        // Show estimated pricing before API call
                        <>
                          <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "10px",
                            fontSize: "16px",
                          }}>
                            <span>Subtotal</span>
                            <span>${getTotalPrice().toFixed(2)}</span>
                          </div>
                          <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "15px",
                            fontSize: "16px",
                            color: "#666",
                          }}>
                            <span>
                              Shipping
                              {selectedShippingOption && (
                                <span style={{ fontSize: "14px", marginLeft: "5px" }}>
                                  ({selectedShippingOption.replace(/_/g, " ")})
                                </span>
                              )}
                            </span>
                            <span>${shippingCost.toFixed(2)}</span>
                          </div>
                          <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "18px",
                            fontWeight: "600",
                            paddingTop: "15px",
                            borderTop: "1px solid #ddd",
                          }}>
                            <span>Estimated Total</span>
                            <span>${(getTotalPrice() + shippingCost).toFixed(2)}</span>
                          </div>
                        </>
                      )}
                    </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          <Footer />
        </div>
        <StartButton />
      </div>
    </>
  );
};

export default Checkout;
